import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Chip, Fade, styled } from "@mui/material";
import { DocQuestion } from "../DocQuestion";
import { useTranslate } from "../../../hooks/useTranslate";
import { QuestionsIF } from "../QuestionsIF";
import { FeatureWithName } from "fe-web-components/autocomplete";
import {
  SearchComponent,
  UserEnterSymptomsMode,
} from "../../search/searchComponent";
import { QualifiedFeature } from "fe-common/graphql";
import { PatientColorMap } from "../../../layout/patientColorMap";
import { DurationPicker } from "../rangeQuestions/numericQuestions/ui(Views)/durationPicker";
import { GenericButton } from "../../uiControllers/genericButton";
import { buildDurationQF } from "../rangeQuestions/numericQuestions/logic(controllerHooks)/singeValueRangeBuilder";
import { RecursivePartial } from "fe-common/commonTypes/types";
import { onRtl } from "fe-web-components/rtl/rtlStyles";
import clsx from "clsx";
import { useClinicData } from "../../../clinic/clinicHooks";
import { HELIX_CLINIC_ID } from "../../../hooks/usePartnerTheme";

//region [[ Styles ]]

const MultipleComplaintsQuestionView = styled("div")({
  display: "flex",
  width: "100%",
  flexDirection: "column",
  "&:not(:last-of-type)": {
    padding: "5px 0",
  },
  "&:last-of-type": {
    paddingTop: "5px",
  },
});

const FeatureSearch = styled(SearchComponent)({
  "& .search-view": {
    background: "white",
    borderRadius: "4px",
    border: `1px solid ${PatientColorMap.DefaultDark}`,
  },
  "& .ak-feature-selection-input": {
    padding: "5px 12px",
  },
  "&.disabled .ak-feature-selection-input": {
    color: PatientColorMap.DefaultDark,
  },
  "& .list-container": {
    background: "white",
    border: "none",
    boxShadow: "0px 6px 18px 0px #0000001A",
    borderBottomLeftRadius: "10px",
    borderBottomRightRadius: "10px",
  },
  "& .suggestion:hover": {
    background: PatientColorMap.DefaultLight,
  },
  "& [rounded='true']": {
    opacity: "0.8",
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  "& .enter-user-symptom-view": {
    marginTop: 0,
  },
  "& .enter-user-symptom-view .patient-MuiOutlinedInput-root.patient-Mui-focused .MuiOutlinedInput-notchedOutline, & .enter-user-symptom-view .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
});

const ComplaintsContainer = styled("div")({
  display: "flex",
  flexFlow: "row",
  marginTop: "10px",
  marginBottom: "15px",
  padding: "0 22px",
  "&.squeezed": {
    height: 0,
    margin: "0 5px",
  },
  flexWrap: "wrap",
  transition: "all 0.3s ease-in-out",
});

const PromptContainer = styled(ComplaintsContainer)({
  flexFlow: "column",
  marginTop: "12px",
  padding: "0 25px",
  "& .search-component-view ": {
    marginBottom: "7px",
  },
});

const DurationContainer = styled("div")({
  display: "flex",
  flexFlow: "column",
  flex: 2,
});

const FloatingSubmitButtonContainer = styled("div")({
  position: "static",
  bottom: "22px",
  left: "0",
  display: "flex",
  flexFlow: "row",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
});

const AddButtonWrapper = styled("div")({
  display: "flex",
  flexFlow: "row-reverse",
});

const AddButton = styled(GenericButton)({
  "&.generic-button, &.generic-button:hover, &.generic-button:focus": {
    height: "33px",
    minWidth: "fit-content",
    marginTop: "16px",
    marginBottom: "0",
    padding: "0 25px",
  },
  "&.add-button, &.add-button:hover, &.add-button:focus": {
    fontSize: "12px",
  },
});

const SubmitButton = styled(AddButton)({
  width: "100%",
  "&.generic-button, &.generic-button:hover, &.generic-button:focus": {
    height: "44px",
  },
});

const SearchComponentLabel = styled("div")({
  fontWeight: 700,
  fontSize: "15px",
  lineHeight: "19px",
  color: PatientColorMap.Default,
});

const DurationLabel = styled(SearchComponentLabel)({
  marginBottom: "10px",
});

const DurationHighlight = styled("div")({
  padding: "9px 40px",
  margin: "0 -40px",
  borderRadius: "12px",
  "&.highlight": {
    background: "#f6f6f6",
  },
});

const ComplaintChip = styled(Chip)(({ theme }) => ({
  margin: "2px",
  "&, &:focus": {
    background: PatientColorMap.PrimaryLight,
    "&:not(.current, &:hover) svg path": {
      color: PatientColorMap.Disabled,
    },
  },
  "&.current, &:hover": {
    background: PatientColorMap.PrimaryDark,
    color: PatientColorMap.White,
    "& svg path, &:hover svg path": {
      color: PatientColorMap.White,
    },
  },
  [theme.breakpoints.down("xs")]: {
    "&:not(.current):hover": {
      background: PatientColorMap.PrimaryLight,
      color: PatientColorMap.Default,
      "& svg path, &:hover svg path": {
        color: PatientColorMap.Disabled,
      },
    },
  },
  "& > svg": {
    margin: "0 5px 0 -6px",
    ...onRtl({
      margin: "0 -6px 0 5px",
    }),
  },
}));

//endregion [[ Styles ]]

//region [[ Props ]]

export interface MultipleComplaintsQuestionProps extends QuestionsIF {}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const MultipleComplaintsQuestion = ({
  ...props
}: MultipleComplaintsQuestionProps) => {
  const { translate } = useTranslate();
  const [complaints, setComplaints] = useState<
    RecursivePartial<QualifiedFeature>[]
  >([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const complaintsRef = useRef<HTMLDivElement>();
  const [dirty, setDirty] = useState(false);
  const isMountedRef = useRef(false);
  const durationInputRef = useRef<HTMLDivElement>(null);
  const { clinic: clinicId } = useClinicData();

  // Make sure the setTimeout scroll doesnt scroll if the component is no longer on DOM
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const [currentComplaint, setCurrentComplaint] = useState<
    RecursivePartial<QualifiedFeature>
  >(null);

  const handleAddCurrentFeature = (e) => {
    const qfWithDuration = currentComplaint;
    setCurrentComplaint(null);
    setComplaints((olderComplaints) => {
      let index = currentIndex === -1 ? olderComplaints.length : currentIndex;
      setCurrentIndex(-1);
      return [
        ...olderComplaints.slice(0, index),
        qfWithDuration,
        ...olderComplaints.slice(index + 1),
      ];
    });
    setDirty(false);
  };

  useEffect(() => {
    if (complaintsRef.current) {
      setTimeout(() => {
        if (!isMountedRef.current || !complaintsRef.current) return;
        complaintsRef.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "start",
          inline: "start",
        });
      }, 500);
    }
  }, [complaints]);

  const handleSubmitClicked = (e) => {
    // console.log("Submitting complaints", complaints);
    props.onAnswer(complaints as QualifiedFeature[]);
  };

  const handleFeatureSelected = (event, featureWithName: FeatureWithName) => {
    if (featureWithName?.value) {
      const qualifiedFeature = featureWithName.value;
      setCurrentComplaint(qualifiedFeature);
    }
    const input = durationInputRef.current
      .getElementsByTagName("input")
      ?.item?.(0);
    // Focus on duration input
    input?.focus?.();
  };

  const handleDurationSelected = useCallback(
    (qfWithDuration: QualifiedFeature) => {
      const durationQualifierIndex = qfWithDuration.qualifiers.findIndex(
        (q) => !!q.range,
      );
      const qualifiersWithoutDuration = [
        ...qfWithDuration.qualifiers.slice(0, durationQualifierIndex),
        ...qfWithDuration.qualifiers.slice(durationQualifierIndex + 1),
      ];

      const qfWithRelation = buildDurationQF(
        { ...qfWithDuration, qualifiers: qualifiersWithoutDuration },
        qfWithDuration.qualifiers[durationQualifierIndex].range,
      );

      setCurrentComplaint((current) => (!current ? null : qfWithRelation));
      setDirty(true);
    },
    [],
  );

  const createDeleteComplaintHandler = (i) => (e) => {
    e.stopPropagation();
    setComplaints((currentComplaints) => [
      ...currentComplaints.slice(0, i),
      ...currentComplaints.slice(i + 1),
    ]);
    setCurrentIndex(-1);
  };

  const createComplaintClickHandler = (i) => (e) => {
    e.stopPropagation();
    setCurrentIndex((current) => (current === i ? -1 : i));
    setCurrentComplaint((current) =>
      current === complaints[i] ? null : complaints[i],
    );
  };

  const handleClearSelection = (e) => {
    setCurrentIndex(-1);
    setCurrentComplaint(null);
  };

  const SEARCH_TEXT =
    currentIndex > -1
      ? "MULTIPLE_COMPLAINT_EDIT_MAIN_COMPLAINT"
      : !complaints?.length
      ? "MULTIPLE_COMPLAINT_MAIN_COMPLAINT_TEXT"
      : "MULTIPLE_COMPLAINT_NEXT_COMPLAINT_TEXT";

  const DURATION_TEXT = "DURATION_TEXT";

  // Hack to disable scroll of the page in helix when selecting duration
  const isHelix = clinicId === HELIX_CLINIC_ID;
  return (
    <MultipleComplaintsQuestionView
      className={"multiple-complaints-question"}
      ref={complaintsRef}
    >
      <DocQuestion text={translate("QUESTION_MULTIPLE_COMPLAINT")} />
      {!props.answer && (
        <>
          <ComplaintsContainer
            className={complaints.length ? "open" : "squeezed"}
            onClick={handleClearSelection}
          >
            {complaints.map((complaint, i) => (
              <ComplaintChip
                className={currentIndex === i ? "current" : ""}
                key={i}
                label={complaint.feature.shortname}
                onDelete={createDeleteComplaintHandler(i)}
                onClick={createComplaintClickHandler(i)}
              />
            ))}
          </ComplaintsContainer>
          <PromptContainer>
            <SearchComponentLabel>
              {translate(SEARCH_TEXT)}
            </SearchComponentLabel>
            <FeatureSearch
              selected={currentComplaint?.feature?.shortname}
              onChange={handleFeatureSelected}
              userEnterSymptom={UserEnterSymptomsMode.NONE}
              searchOptions={{ categories: [], patientFacing: true }}
              disabled={currentIndex >= 0}
              noIcon
            />
            <DurationHighlight className={currentIndex >= 0 ? "highlight" : ""}>
              <DurationLabel>{translate(DURATION_TEXT)}:</DurationLabel>
              <DurationContainer
                className={clsx(currentComplaint ? "active" : "disabled")}
              >
                <DurationPicker
                  attachToFeature={
                    (currentComplaint as QualifiedFeature) || null
                  }
                  onDurationPicked={handleDurationSelected}
                  disabled={!currentComplaint}
                  copyDurationFrom={
                    complaints.length &&
                    (complaints.slice(-1).pop() as QualifiedFeature)
                  }
                  durationInputRef={durationInputRef}
                  isEmbedded={isHelix}
                />
                <AddButtonWrapper>
                  <AddButton
                    disabled={!dirty}
                    onClick={handleAddCurrentFeature}
                    label={translate(
                      currentIndex >= 0 ? "UPDATE_BUTTON_TEXT" : "ADD",
                    )}
                    allowReclicking
                  />
                </AddButtonWrapper>
              </DurationContainer>
            </DurationHighlight>
            <Fade in={!!complaints?.length} mountOnEnter unmountOnExit>
              <FloatingSubmitButtonContainer>
                <SubmitButton
                  disabled={!complaints?.length}
                  label={translate("ANSWER_DONE")}
                  onClick={handleSubmitClicked}
                />
              </FloatingSubmitButtonContainer>
            </Fade>
          </PromptContainer>
        </>
      )}
    </MultipleComplaintsQuestionView>
  );
};
